/* eslint-disable @typescript-eslint/no-explicit-any */
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { EColumnTypeEnum } from '@core/enum/column-type-enum';
import { EPossibleActionsEnum } from '@core/enum/possible-actions-enum';
import { ESortOrderEnum } from '@core/enum/sort-order-enum';
import { ColumnItem } from '@core/interface/column-item';
import { FilterItem } from '@core/interface/filter-item';
import { QueryParamsItem } from '@core/interface/query-params-item';
import { SortOrderItem } from '@core/interface/sort-order-item';
import { TableStateDto } from '@core/interface/table-state-dto';
import { FeatureFlagsService } from '@core/service/feature-flags/feature-flags.service';
import { UtilService } from '@core/service/util.service';
import { CustomDatePipe } from '@shared/pipes/custom-date.pipe';

@Component({
  selector: 'ldc-filter-pagi-table',
  templateUrl: './filter-pagi-table.component.html',
  styleUrl: './filter-pagi-table.component.scss'
})
export class FilterPagiTableComponent<T> {
  @Input() tableState: TableStateDto<T> = {
    data: [],
    totalCount: 0,
    hasNextPage: false,
    hasPreviousPage: false,
    pageNumber: 1,
    pageSize: 10,
    loading: true
  };
  @Input() columns: ColumnItem[] = [];
  @Output() queryParamsChange = new EventEmitter<QueryParamsItem>();
  @Output() actionTriggered = new EventEmitter<{ action: EPossibleActionsEnum; item: T }>();

  searchValue: { [key: string]: string } = {};
  filterItems: FilterItem[] = [];

  EColumnTypeEnum = EColumnTypeEnum;
  EPossibleActionsEnum = EPossibleActionsEnum;

  constructor(
    private utilService: UtilService,
    private featureFlagsService: FeatureFlagsService,
    private customDatePipe: CustomDatePipe
  ) {}

  onQueryParamsChange(params: any, column?: string, resetSearch?: boolean): void {
    const { pageSize, pageIndex, filter, sort } = params;
    if (column && !resetSearch) {
      this.filterItems.push(this.searchFilterItem(column));
    } else if (column && resetSearch) {
      this.searchValue[column] = '';
      const searchTermToRemove = this.filterItems.find(f => f.PropertyName === column);
      if (searchTermToRemove) this.filterItems.splice(this.filterItems.indexOf(searchTermToRemove), 1);
      // Sorting indicator turned off
      this.columns = this.columns.map(c => {
        if (c.key === column) {
          return { ...c, sorting: undefined };
        }
        return c;
      });
    } else {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if (filter.some((f: any) => f.value !== null && f.value.length > 0)) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        filter.forEach((filter: any) => {
          const existingSearchFilterItem = this.filterItems.find(fi => fi.PropertyName === filter.key);
          if (existingSearchFilterItem) {
            existingSearchFilterItem.Value = filter.value;
            this.filterItems.splice(this.filterItems.indexOf(existingSearchFilterItem), 1, existingSearchFilterItem);
          } else {
            const newFilter: FilterItem = {
              PropertyName: filter.key,
              Value: filter.value
            };
            this.filterItems.push(newFilter);
          }
        });
      } else if (filter.some((f: any) => f.value === null || f.value.length === 0)) {
        const filterToRemove = filter.find((f: any) => f.value === null || f.value.length === 0);
        const filterItemToRemove = this.filterItems.find(fi => fi.PropertyName === filterToRemove.key);
        if (filterToRemove && filterItemToRemove)
          this.filterItems.splice(this.filterItems.indexOf(filterItemToRemove), 1);
      }
    }
    const stringifiedFilterItems = this.utilService.changeItemToJsonString(this.filterItems);
    const sortOrder: SortOrderItem = {};
    if (sort) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if (sort.some((s: any) => s.value === ESortOrderEnum.DESECENDING)) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const sorting = sort.find((s: any) => s.value === ESortOrderEnum.DESECENDING);
        sortOrder.PropertyName = this.utilService.capitilizeFirstLetter(sorting.key);
        sortOrder.Value = false;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } else if (sort.some((s: any) => s.value === ESortOrderEnum.ASSCENDING)) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const sorting = sort.find((s: any) => s.value === ESortOrderEnum.ASSCENDING);
        sortOrder.PropertyName = this.utilService.capitilizeFirstLetter(sorting.key);
      }
    }
    this.queryParamsChange.emit({ pageIndex, pageSize, filters: stringifiedFilterItems, sortOrder: sortOrder });
  }

  searchFilterItem(column: string): FilterItem {
    const filterValue = this.searchValue[column];
    const existingSearchFilterItem = this.filterItems.find(fi => fi.PropertyName === column);
    if (existingSearchFilterItem) {
      existingSearchFilterItem.Value = filterValue;
      this.filterItems.splice(this.filterItems.indexOf(existingSearchFilterItem), 1);
      return existingSearchFilterItem;
    } else {
      const newFilter: FilterItem = {
        PropertyName: column,
        Value: filterValue
      };
      return newFilter;
    }
  }

  setDeleteActionPerUserRole(item: any, action: EPossibleActionsEnum): boolean {
    return this.utilService.setDeleteActionPerUserRole(item, action);
  }

  getItemValue(item: any, key: string): string {
    const value = this.utilService.getItemValue(item, key);
    const column = this.columns.find(c => c.key === key);

    if (column && column.columnType === EColumnTypeEnum.DATE && value) {
      return this.customDatePipe.transform(value) || value;
    }

    if (column && column.columnType === EColumnTypeEnum.BOOLEAN_BADGE && value) {
      if (typeof value === 'boolean') {
        return value ? 'success' : 'error';
      }

      if (typeof value === 'string') {
        return value.toLowerCase() === 'ok' ? 'success' : 'error';
      }
    }

    return value;
  }

  triggerAction(action: EPossibleActionsEnum, item: T): void {
    this.actionTriggered.emit({ action, item });
  }

  isFeatureEnabled(featureKey: string): boolean {
    return this.featureFlagsService.isFeatureEnabled(featureKey);
  }
}
